import React, { useEffect, useMemo, useState } from 'react';
import { BrowserRouter, useNavigate, useLocation } from 'react-router-dom';
import { IBTickerTape } from './components/IBTickerTape';
import SystemControlButton from './components/SystemControlButton';
import GatewayControlButton from './components/GatewayControlButton';

// ... (existing imports)



import ManualOrderEntry from './components/ManualOrderEntry';
import {
  buildPreparedNQMMOrderState,
  type PreparedNQMMOrder,
  type PreparedNQMMOrderState,
} from './components/ManualOrderEntry/preparedOrder';
import AccountSwitcher from './components/AccountSwitcher';
import UserTypeBadge from './components/UserTypeBadge';
import { Activity, Briefcase, Home, Settings, List, BarChart3, LogOut, User, Shield, ChevronLeft, ChevronRight, PieChart, Bell, FileText, Database, ListChecks, CalendarDays, Plus, X, SlidersHorizontal } from 'lucide-react';
import { tradingApi, Order, SystemControl, SystemStatus as SystemStatusType } from './services/api';
import { AuthProvider, useAuth } from './context/AuthContext';
import NotificationFeed from './components/NotificationFeed';
import TaskDrawer from './components/TaskDrawer';
import GatewayConfigModal from './components/GatewayConfigModal';
import MarketEvents from './components/MarketEvents';
import Modal from './components/Modal';
import { MarketDataProvider } from './market/MarketDataProvider';
import { GlobalErrorProvider } from './components/GlobalErrorProvider';
import AppRoutes from './AppRoutes';
import DevFloatingMenu from './components/DevFloatingMenu';
const Login = React.lazy(() => import('./pages/Login'));
const Register = React.lazy(() => import('./pages/Register'));
const SetupAccountPage = React.lazy(() => import('./pages/SetupAccountPage'));

const logo = new URL('./assets/logo.svg', import.meta.url).href;
const notificationSound = new URL('./assets/notification.mp3', import.meta.url).href;
const pageLoadingFallback = (
  <div className="flex min-h-[40vh] items-center justify-center" role="status">
    <span className="text-sm text-muted">Loading page…</span>
  </div>
);

interface UserContext {
  user_id: number;
  username: string;
  role: string;
  is_superuser: boolean;
  is_acting_as: boolean;
  preferences: {
    notify_email_signal: boolean;
    notify_email_fill: boolean;
    notify_email_error: boolean;
    notify_email_warning: boolean;
    notify_email_system: boolean;
    notify_sound_enabled: boolean;
  };
  original_user?: {
    user_id: number;
    username: string;
    role: string;
  };
}

const LiveClock: React.FC<{ value?: string }> = ({ value }) => {
  const [time, setTime] = useState(() => new Date().toLocaleTimeString());

  useEffect(() => {
    if (value) return;
    const intervalId = window.setInterval(
      () => setTime(new Date().toLocaleTimeString()),
      1000,
    );
    return () => window.clearInterval(intervalId);
  }, [value]);

  return <>{value || time}</>;
};

const AppContent: React.FC = () => {
  const {
    isAuthenticated,
    isUserLoading,
    user,
    userProfileError,
    logout,
    refreshUserProfile,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const [systemStatus, setSystemStatus] = useState<SystemStatusType | null>(null);
  const [systemControlState, setSystemControlState] = useState<SystemControl | null>(null);
  const [systemStatusTs, setSystemStatusTs] = useState<string>('');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const [isNotificationDrawerOpen, setIsNotificationDrawerOpen] = useState(false);
  const [isTaskDrawerOpen, setIsTaskDrawerOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeTaskCount, setActiveTaskCount] = useState(0);
  const [userContext, setUserContext] = useState<UserContext | null>(null);
  const [isSettingsDropdownOpen, setIsSettingsDropdownOpen] = useState(false);
  const [isGatewayConfigModalOpen, setIsGatewayConfigModalOpen] = useState(false);
  const [isEventsModalOpen, setIsEventsModalOpen] = useState(false);
  const [isManualOrderModalOpen, setIsManualOrderModalOpen] = useState(false);
  const [preparedNQMMOrder, setPreparedNQMMOrder] = useState<PreparedNQMMOrderState | null>(null);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);

  // Ref for sound throttling and change detection
  const prevUnreadCount = React.useRef(0);
  const lastSoundPlayedAt = React.useRef(0);
  const notificationAudio = useMemo(() => new Audio(notificationSound), []);

  useEffect(() => {
    const handlePreparedNQMMOrder = (event: Event) => {
      const detail = (event as CustomEvent<PreparedNQMMOrder>).detail;
      if (!detail?.openInModal) return;

      const order = buildPreparedNQMMOrderState(detail);
      if (!order) return;
      setPreparedNQMMOrder(order);
      setIsManualOrderModalOpen(true);
    };

    window.addEventListener('nqmm-prepare-order', handlePreparedNQMMOrder);
    return () => window.removeEventListener('nqmm-prepare-order', handlePreparedNQMMOrder);
  }, []);

  const openManualOrderModal = () => {
    setPreparedNQMMOrder(null);
    setIsManualOrderModalOpen(true);
  };

  const closeManualOrderModal = () => {
    setIsManualOrderModalOpen(false);
    setPreparedNQMMOrder(null);
  };

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated && location.pathname !== '/login' && location.pathname !== '/register' && location.pathname !== '/setup-account') {
      navigate('/login');
    }
  }, [isAuthenticated, location.pathname, navigate]);

  // Lightweight system status poller for header/sidebar badges
  const fetchSystemStatus = async () => {
    if (!isAuthenticated) return;
    try {
      const response = await tradingApi.getSystemControlState();
      if (response.success && response.data) {
        setSystemControlState(response.data);
        setSystemStatus({
          success: true,
          data: {
            running: response.data.system_enabled,
            ib_connected: response.data.ib_connected,
            version: response.data.version,
            timestamp: response.data.timestamp
          },
          timestamp: response.data.timestamp
        });
        setSystemStatusTs(new Date().toLocaleTimeString());
      }
    } catch {
      // ignore header status errors
    }
  };

  const fetchUnreadCount = async () => {
    if (!isAuthenticated) return;
    try {
      const res = await tradingApi.getUnreadCount();
      if (res.success) setUnreadCount(res.data.count);
    } catch {
      // ignore
    }
  };

  const fetchTaskCount = async () => {
    if (!isAuthenticated || user?.role === 'investor') return;
    try {
      const tasks = await tradingApi.getDataDownloadTasks();
      setActiveTaskCount(tasks.filter((task) => ['queued', 'running', 'cancelling'].includes(task.status)).length);
    } catch {
      // ignore
    }
  };

  const fetchUserContext = async () => {
    if (!isAuthenticated) return;
    try {
      const res = await tradingApi.getCurrentContext();
      if (res.success) setUserContext(res.data as UserContext);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    if (isAuthenticated && !isUserLoading) {
      fetchSystemStatus();
      fetchUnreadCount();
      fetchTaskCount();
      fetchUserContext();
      const id = setInterval(() => {
        fetchSystemStatus();
        fetchUnreadCount();
        fetchTaskCount();
        fetchUserContext();
      }, 10000); // Poll faster (10s) for responsive notifications
      return () => clearInterval(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isUserLoading, user?.role]);

  // Sound Effect Trigger
  useEffect(() => {
    if (unreadCount > prevUnreadCount.current) {
      const soundEnabled = userContext?.preferences?.notify_sound_enabled ?? true;
      if (soundEnabled) {
        const now = Date.now();
        if (now - lastSoundPlayedAt.current > 500) {
          notificationAudio.play().catch(() => {
            // Ignore audio play errors (e.g. browser interaction policy)
          });
          lastSoundPlayedAt.current = now;
        }
      }
    }
    prevUnreadCount.current = unreadCount;
  }, [unreadCount, userContext, notificationAudio]);

  const navItems = useMemo(
    () => {
      const items = [
        { label: 'Dashboard', path: '/', icon: Home },
      ];

      if (user?.role === 'investor') {
        items.push({ label: 'Shared Backtests', path: '/backtest', icon: Activity });
      }

      // Investor backtests are read-only and show only accessible shared results.
      if (user?.role !== 'investor') {
        items.push(
          { label: 'Strategies', path: '/strategies', icon: BarChart3 },
          { label: 'Backtest', path: '/backtest', icon: Activity },
          { label: 'Data', path: '/data', icon: Database },
          { label: 'Positions', path: '/positions', icon: Briefcase },
          { label: 'Managed ORB', path: '/managed-orb', icon: SlidersHorizontal },
          { label: 'Portfolio', path: '/portfolio', icon: PieChart },
          { label: 'Trade Logs', path: '/trades', icon: List }
        );
      }

      return items;
    },
    [user?.role]
  );

  const handleNavigate = (path: string) => {
    navigate(path);
    setNavOpen(false);
  };

  if (location.pathname === '/login') {
    return <React.Suspense fallback={pageLoadingFallback}><Login /></React.Suspense>;
  }

  if (location.pathname === '/register') {
    return <React.Suspense fallback={pageLoadingFallback}><Register /></React.Suspense>;
  }

  if (location.pathname.startsWith('/setup-account')) {
    return <React.Suspense fallback={pageLoadingFallback}><SetupAccountPage /></React.Suspense>;
  }

  if (!isAuthenticated) {
    return null; // or loading spinner while redirecting
  }

  if (isUserLoading) {
    return (
      <div className="min-h-screen auth-shell flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-neon/30 border-t-neon rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated && !user) {
    return (
      <div className="min-h-screen auth-shell flex items-center justify-center p-4 text-foreground">
        <div className="auth-card max-w-md space-y-4 p-6 text-center">
          <h1 className="text-lg font-bold">Account profile unavailable</h1>
          <p className="text-sm text-muted">{userProfileError || 'The server could not confirm this account.'}</p>
          <div className="flex justify-center gap-3">
            <button className="rounded-lg border border-neon/40 px-4 py-2 text-sm text-neon" onClick={refreshUserProfile}>
              Try again
            </button>
            <button className="rounded-lg border border-white/20 px-4 py-2 text-sm text-white/70" onClick={logout}>
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen app-shell text-foreground relative overflow-hidden">
      {/* Global Top Bar */}
      <header className={`fixed top-0 right-0 z-30 topbar border-b transition-all duration-300 ease-in-out ${isSidebarCollapsed ? 'left-20' : 'left-60'} ${navOpen ? 'left-0' : ''} lg:block hidden`}>
        <div className="w-full px-4 lg:px-6 py-2 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 sm:gap-3 xl:gap-5 min-w-0">
            <div className="flex items-center gap-2 xl:gap-4 text-[10px] font-bold tracking-tight uppercase shrink-0">
              <div className={`flex items-center gap-1.5 xl:gap-2 px-2 xl:px-2.5 py-1 rounded-lg border ${systemStatus?.data?.running
                ? systemStatus?.data?.ib_connected
                  ? 'bg-success-50 border-success-500/20 text-success-500'
                  : 'bg-warning-50 border-warning-500/20 text-warning-500'
                : 'bg-error-50 border-error-500/20 text-error-500'
                }`}>
                <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${systemStatus?.data?.running
                  ? systemStatus?.data?.ib_connected
                    ? 'bg-success-500 animate-pulse'
                    : 'bg-warning-500 animate-pulse'
                  : 'bg-error-500'
                  }`} />
                <span className="whitespace-nowrap">
                  {systemStatus?.data?.running
                    ? systemStatus?.data?.ib_connected
                      ? (<><span className="hidden xl:inline">Trading </span>Active</>)
                      : (<><span className="hidden xl:inline">Trading </span>Stopped</>)
                    : (<><span className="hidden xl:inline">Trading </span>Disabled</>)}
                </span>
              </div>
              <div className="flex items-center gap-1.5 xl:gap-2 px-2 xl:px-2.5 py-1 rounded-lg bg-info-50 border border-info-500/20">
                <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${systemStatus?.data?.ib_connected ? 'bg-info-500 animate-pulse' : 'bg-error-500'}`} />
                <span className={`whitespace-nowrap ${systemStatus?.data?.ib_connected ? 'text-info-500' : 'text-error-500'}`}>
                  <span className="hidden xl:inline">Connection </span>{systemStatus?.data?.ib_connected ? 'Sync' : 'Lost'}
                </span>
              </div>
            </div>

            <div className="h-4 w-px divider shrink-0" />

            <div className="flex items-center gap-2 shrink-0">
              {user?.role !== 'investor' && (
                <SystemControlButton
                  controlState={systemControlState}
                  onRefresh={fetchSystemStatus}
                  onError={(err: string) => console.error(err)}
                  variant="slim"
                  iconSize={13}
                  className="h-7 text-[11px] px-2.5 gap-1.5 shrink-0"
                />
              )}
              <GatewayControlButton
                controlState={systemControlState}
                gatewayId={systemControlState?.saved_gateways?.find((g) => g.is_active)?.id}
                onRefresh={fetchSystemStatus}
                onConfigureNeeded={() => setIsGatewayConfigModalOpen(true)}
                onError={(err: string) => console.error(err)}
                variant="slim"
                iconSize={13}
                className="h-7 text-[11px] px-2.5 gap-1.5 shrink-0"
              />
              <div className="relative shrink-0">
                <button
                  onClick={() => setIsSettingsDropdownOpen(!isSettingsDropdownOpen)}
                  className={`h-8 w-8 flex items-center justify-center rounded-lg icon-action ${isSettingsDropdownOpen ? 'bg-neon text-black border-neon shadow-neon/20' : ''}`}
                  title="System Settings"
                >
                  <Settings className="w-4 h-4" />
                </button>

                {isSettingsDropdownOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setIsSettingsDropdownOpen(false)}
                    />
                    <div className="absolute top-full mt-2 right-0 w-56 panel-surface z-50 animate-in fade-in slide-in-from-top-2">
                      <div className="p-2 space-y-1">
                        <button
                          onClick={() => {
                            navigate('/control');
                            setIsSettingsDropdownOpen(false);
                          }}
                          className="menu-item"
                        >
                          <Settings className="w-4 h-4 text-neon" />
                          Advanced Control
                        </button>

                        {user?.role === 'admin' && (
                          <button
                            onClick={() => {
                              navigate('/admin');
                              setIsSettingsDropdownOpen(false);
                            }}
                            className="menu-item"
                          >
                            <Shield className="w-4 h-4 text-emerald-400" />
                            Admin Dashboard
                          </button>
                        )}

                        {user?.role === 'admin' && (
                          <button
                            onClick={() => {
                              navigate('/admin/logs');
                              setIsSettingsDropdownOpen(false);
                            }}
                            className="menu-item"
                          >
                            <FileText className="w-4 h-4 text-neon" />
                            Logs
                          </button>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1.5 xl:gap-2 shrink-0">
            {user?.role !== 'investor' && (
              <button
                onClick={openManualOrderModal}
                className="relative h-8 w-8 flex items-center justify-center rounded-lg icon-action group shrink-0"
                title="Place manual order"
                aria-label="Place manual order"
              >
                <Plus className="w-4 h-4 group-hover:scale-110 transition-transform" />
              </button>
            )}
            {user?.role !== 'investor' && (
              <button
                onClick={() => setIsTaskDrawerOpen(true)}
                className="relative h-8 w-8 flex items-center justify-center rounded-lg icon-action group shrink-0"
                title="Background tasks"
              >
                <ListChecks className="w-4 h-4 group-hover:scale-110 transition-transform" />
                {activeTaskCount > 0 && (
                  <span className="absolute top-1.5 right-1.5 min-w-3.5 h-3.5 px-1 bg-neon text-black text-[9px] font-bold rounded-full flex items-center justify-center border border-surface shadow-[0_0_8px_rgba(var(--neon-rgb),0.5)]">
                    {activeTaskCount > 9 ? '9+' : activeTaskCount}
                  </span>
                )}
              </button>
            )}

            {/* Notifications Bell */}
            <button
              onClick={() => setIsNotificationDrawerOpen(true)}
              className="relative h-8 w-8 flex items-center justify-center rounded-lg icon-action group shrink-0"
            >
              <Bell className="w-4 h-4 group-hover:scale-110 transition-transform" />
              {unreadCount > 0 && (
                <span className="absolute top-1.5 right-1.5 w-3.5 h-3.5 bg-error-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center border border-surface shadow-[0_0_8px_rgba(var(--danger-rgb),0.5)]">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            <div className="h-6 w-px divider mx-1 xl:mx-2 shrink-0" />

            {/* Combined Account & User Group */}
            <div className="h-8 flex items-center gap-1.5 xl:gap-2 pl-1.5 pr-1 rounded-lg panel-section border backdrop-blur-sm shrink-0">
              {/* Account Selector */}
              <div className="flex items-center shrink-0">
                <AccountSwitcher variant="compact" />
              </div>

              <div className="h-4 w-px divider shrink-0" />

              {/* User Profile */}
              <div className="flex items-center gap-2 min-w-0">
                <div className="flex flex-col items-end justify-center leading-none min-w-0 max-w-[80px] xl:max-w-[140px]">
                  <p
                    className="text-[10px] font-black text-foreground leading-none tracking-tight truncate w-full text-right"
                    title={userContext?.is_acting_as
                      ? `${userContext.original_user?.username} (${userContext.username})`
                      : user?.username}
                  >
                    {userContext?.is_acting_as
                      ? `${userContext.original_user?.username} (${userContext.username})`
                      : user?.username}
                  </p>
                  <div className="mt-0.5 flex items-center justify-end gap-1">
                    <UserTypeBadge
                      role={userContext?.role || user?.role}
                      compact
                    />
                    {userContext?.is_acting_as && (
                      <span className="rounded-full border border-amber-500/25 bg-amber-500/10 px-1.5 py-0.5 text-[8px] font-black uppercase leading-none tracking-widest text-amber-400">
                        Manager
                      </span>
                    )}
                  </div>
                </div>
                <div className={`w-6 h-6 rounded-md flex items-center justify-center border transition-all shrink-0 ${userContext?.is_acting_as
                  ? 'bg-amber-500/10 border-amber-500/25 shadow-[0_0_15px_rgba(var(--warning-rgb),0.1)]'
                  : 'bg-neon/10 border-neon/20 shadow-[0_0_15px_rgba(var(--neon-rgb),0.1)]'
                  }`}>
                  <User className={`w-3 h-3 ${userContext?.is_acting_as ? 'text-amber-400' : 'text-neon'}`} />
                </div>
                <button
                  onClick={logout}
                  className="w-7 h-7 flex items-center justify-center rounded-md text-muted hover:text-error-500 hover:bg-error-50/10 transition group shrink-0"
                  title="Sign Out"
                >
                  <LogOut className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" />
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="w-full border-t border-white/5 bg-surface px-4 lg:px-6 py-1">
          <div className="relative flex items-center justify-center">
            <IBTickerTape />
            {user?.role !== 'investor' && (
              <button
                onClick={() => setIsEventsModalOpen(true)}
                className="absolute right-0 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md border border-neon/25 bg-neon/10 text-neon transition-colors hover:border-neon/50 hover:bg-neon/20"
                title="Market events"
              >
                <CalendarDays className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Mobile header (Updated for Top Bar consistency) */}
      <header className="sticky top-0 z-30 topbar border-b lg:hidden px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex shrink-0 items-center gap-3">
            <button
              className="h-8 w-8 flex items-center justify-center rounded-md border border-white/10 bg-transparent text-muted shadow-none transition hover:border-neon/40 hover:bg-neon/5 hover:text-neon"
              onClick={() => setNavOpen(!navOpen)}
              aria-label={navOpen ? 'Close navigation' : 'Open navigation'}
            >
              <div className="space-y-1.5 w-5">
                <span className={`block h-0.5 w-full bg-current transition ${navOpen ? 'translate-y-2 rotate-45' : ''}`} />
                <span className={`block h-0.5 w-full bg-current transition ${navOpen ? 'opacity-0' : ''}`} />
                <span className={`block h-0.5 w-full bg-current transition ${navOpen ? '-translate-y-2 -rotate-45' : ''}`} />
              </div>
            </button>
            <img 
              src={logo} 
              alt="Logo" 
              className="w-8 h-8 rounded-lg cursor-pointer hover:scale-105 transition-transform" 
              onClick={() => handleNavigate('/')}
              title="Go to Dashboard"
            />
          </div>
          <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5">
            <AccountSwitcher variant="compact" showIdentity alwaysRender />
            {user?.role !== 'investor' && (
              <>
              <button
                onClick={openManualOrderModal}
                className="relative inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-neon/25 bg-neon/10 text-neon transition-colors hover:border-neon/50 hover:bg-neon/20"
                title="Place manual order"
                aria-label="Place manual order"
              >
                <Plus className="h-4 w-4" />
              </button>
              <button
                onClick={() => setIsEventsModalOpen(true)}
                className="relative inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-neon/25 bg-neon/10 text-neon transition-colors hover:border-neon/50 hover:bg-neon/20"
                title="Market events"
              >
                <CalendarDays className="h-4 w-4" />
              </button>
              <button
                onClick={() => setIsTaskDrawerOpen(true)}
                className="icon-action relative inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md"
              >
                <ListChecks className="h-4 w-4" />
                {activeTaskCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-4 h-4 px-1 bg-neon text-black text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-surface">
                    {activeTaskCount > 9 ? '9+' : activeTaskCount}
                  </span>
                )}
              </button>
              </>
            )}
            <button
              onClick={() => setIsNotificationDrawerOpen(true)}
              className="icon-action relative inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md"
            >
              <Bell className="h-4 w-4" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-error-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-surface">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
          </div>
        </div>

        <div className="mt-2 grid grid-cols-2 items-stretch gap-2 border-t border-white/10 pt-2">
          {user?.role !== 'investor' && (
            <SystemControlButton
              controlState={systemControlState}
              onRefresh={fetchSystemStatus}
              onError={(err: string) => console.error(err)}
              variant="slim"
              iconSize={13}
              className="h-8 min-w-0 w-full rounded-md px-2 text-[10px] sm:text-[11px]"
            />
          )}
          <GatewayControlButton
            controlState={systemControlState}
            gatewayId={systemControlState?.saved_gateways?.find((g) => g.is_active)?.id}
            onRefresh={fetchSystemStatus}
            onConfigureNeeded={() => setIsGatewayConfigModalOpen(true)}
            onError={(err: string) => console.error(err)}
            variant="slim"
            iconSize={13}
            className="h-8 min-w-0 w-full rounded-md px-2 text-[10px] sm:text-[11px]"
          />
        </div>
      </header>

      {/* Desktop sidebar */}
      <aside className={`hidden lg:flex fixed top-0 left-0 h-full ${isSidebarCollapsed ? 'w-20' : 'w-60'} flex-col sidebar-shell z-40 transition-all duration-300 ease-in-out border-r`}>
        <div className={`p-4 flex items-center ${isSidebarCollapsed ? 'justify-center' : 'justify-between'} transition-all duration-300`}>
          <div 
            onClick={() => handleNavigate('/')}
            className={`flex items-center gap-3 cursor-pointer group ${isSidebarCollapsed ? 'justify-center w-full' : ''}`}
            title="Go to Dashboard"
          >
            <img src={logo} alt="GetUndiluted Trader" className="w-9 h-9 rounded-lg hover:scale-105 transition-transform" />
            {!isSidebarCollapsed && (
              <div>
                <p className="text-lg text-foreground font-bold tracking-tight group-hover:text-neon transition-colors">GetUndiluted</p>
              </div>
            )}
          </div>
          {!isSidebarCollapsed && (
            <button
              onClick={() => setIsSidebarCollapsed(true)}
              className="h-8 w-8 flex items-center justify-center rounded-md text-muted shadow-none hover:text-foreground hover:bg-neon/5 transition"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Collapsed Toggle (Centered when collapsed) */}
        {isSidebarCollapsed && (
          <div className="w-full flex justify-center mb-4">
            <button
              onClick={() => setIsSidebarCollapsed(false)}
              className="h-8 w-8 flex items-center justify-center rounded-md text-muted shadow-none hover:text-foreground hover:bg-neon/5 transition"
              aria-label="Expand navigation"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}

        <div className="flex-1 -space-y-px overflow-y-auto scrollbar-thin px-2">
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => handleNavigate(item.path)}
              className={`group relative flex items-center transition-all duration-200 ${isSidebarCollapsed 
                ? 'w-10 h-10 justify-center mx-auto rounded-md'
                : 'w-full min-h-9 justify-between rounded-none border-y border-x-0 border-white/10 px-2 py-1.5 text-xs'
                } ${location.pathname === item.path
                  ? 'bg-neon/10 text-neon border-neon/20'
                  : 'text-muted hover:text-foreground hover:bg-neon/10'
                }`}
              title={isSidebarCollapsed ? item.label : ''}
            >
              <span className={`flex items-center gap-2 font-medium tracking-wide ${isSidebarCollapsed ? '' : 'mr-2 text-xs'}`}>
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-none transition-colors">
                  <item.icon className="h-4 w-4" />
                </span>
                {!isSidebarCollapsed && item.label}
              </span>

              {!isSidebarCollapsed && (
                <span className={`h-1.5 w-1.5 rounded-full ${location.pathname === item.path ? 'bg-neon' : 'bg-transparent'}`} />
              )}

              {/* Active Indicator for Collapsed Mode (Left Border or Dot) */}
              {isSidebarCollapsed && location.pathname === item.path && (
                <span className="absolute left-0 top-1/2 -translate-y-1/2 h-7 w-0.5 bg-neon" />
              )}
            </button>
          ))}
        </div>

        <div className={`mt-auto p-4 ${isSidebarCollapsed ? 'px-2' : 'px-6'} flex flex-col items-center gap-2`}>
          <div className="flex flex-col items-center gap-1 text-[9px] text-muted font-mono uppercase tracking-widest mb-2">
            <span className="opacity-70">{systemStatus?.data?.version ? `v${systemStatus.data.version}` : 'Loading...'}</span>
            <span className="opacity-50"><LiveClock value={systemStatusTs} /></span>
          </div>
        </div>
      </aside>

      {/* Mobile drawer */}
      {navOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true" aria-label="Navigation">
          <div
            className="absolute inset-0 overlay-backdrop shadow-[inset_0_0_100px_rgba(var(--overlay-rgb),0.5)]"
            onClick={() => setNavOpen(false)}
          />
          <div className="absolute inset-0 flex h-[100dvh] w-full flex-col sidebar-shell overflow-y-auto p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:p-4 sm:pb-4">
            <div className="flex items-center justify-between gap-3 border-b border-white/10 pb-3">
              <div
              onClick={() => handleNavigate('/')}
              className="flex min-w-0 items-center gap-3 cursor-pointer group"
              title="Go to Dashboard"
              >
                <img src={logo} alt="GetUndiluted Trader" className="h-9 w-9 shrink-0 rounded-lg hover:scale-105 transition-transform" />
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-foreground group-hover:text-neon transition-colors">GetUndiluted Trader</p>
                  <p className="text-[10px] uppercase tracking-[0.16em] text-muted">Navigation</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setNavOpen(false)}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-white/10 text-muted transition hover:border-neon/40 hover:bg-neon/5 hover:text-neon"
                aria-label="Close navigation"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <p className="mb-1 mt-4 px-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted">Navigate</p>
            <div className="-space-y-px">
            {user?.role !== 'investor' && (
              <button
                onClick={() => {
                  setNavOpen(false);
                  setIsTaskDrawerOpen(true);
                }}
              className="w-full border-y border-x-0 border-white/10 px-2 py-1.5 text-left text-xs rounded-none transition text-muted hover:border-neon/20 hover:text-foreground hover:bg-neon/10"
              >
                <span className="flex items-center justify-between text-xs font-semibold uppercase tracking-[0.05em]">
                  <span className="flex items-center gap-2">
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-none">
                      <ListChecks className="h-4 w-4" />
                    </span>
                    Tasks
                  </span>
                  {activeTaskCount > 0 && (
                    <span className="bg-neon text-black text-[10px] px-2 py-0.5 rounded-full">{activeTaskCount}</span>
                  )}
                </span>
              </button>
            )}
            <button
              onClick={() => {
                setNavOpen(false);
                setIsNotificationDrawerOpen(true);
              }}
                className="w-full border-y border-x-0 border-white/10 px-2 py-1.5 text-left text-xs rounded-none transition text-muted hover:border-neon/20 hover:text-foreground hover:bg-neon/10"
            >
              <span className="flex items-center justify-between text-xs font-semibold uppercase tracking-[0.05em]">
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-none">
                    <Bell className="h-4 w-4" />
                  </span>
                  Activity
                </span>
                {unreadCount > 0 && (
                  <span className="bg-error-500 text-white text-[10px] px-2 py-0.5 rounded-full">{unreadCount}</span>
                )}
              </span>
            </button>
            {navItems.map((item) => (
              <button
                key={item.path}
                onClick={() => handleNavigate(item.path)}
                className={`w-full rounded-none border-y border-x-0 border-white/10 px-2 py-1.5 text-left text-xs transition ${location.pathname === item.path
                  ? 'bg-neon/10 text-neon border-neon/20'
                  : 'text-muted hover:text-foreground hover:bg-neon/10'
                  }`}
              >
                <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.05em]">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-none">
                    <item.icon className="h-4 w-4" />
                  </span>
                  {item.label}
                </span>
              </button>
            ))}
            </div>

            <div className="mt-auto border-t border-white/10 pt-3">
              <div className="flex items-center justify-between gap-3 px-2 py-1">
                <div className="flex min-w-0 items-center gap-2">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-success-500/20 bg-success-50 text-success-500">
                    <User className="h-3.5 w-3.5" />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-xs font-semibold text-foreground">{user?.username || 'User'}</p>
                    <div className="mt-1 flex items-center gap-1.5">
                      <UserTypeBadge role={userContext?.role || user?.role} compact />
                      {userContext?.is_acting_as && (
                        <span className="text-[8px] font-bold uppercase tracking-widest text-amber-400">Manager</span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setNavOpen(false);
                    logout();
                  }}
                  className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-error-500/20 px-2.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-error-500 transition hover:bg-error-50 hover:text-error-500"
                >
                  <LogOut className="h-3.5 w-3.5" />
                  <span>Logout</span>
                </button>
              </div>
              <div className="flex items-center justify-between px-2 pt-2 text-[10px] text-muted">
                <span>Last update</span>
                <span className="font-semibold text-foreground"><LiveClock /></span>
              </div>
            </div>
          </div>
        </div>
      )}

      <main className={`relative z-10 min-h-screen w-full transition-all duration-300 ease-in-out ${isSidebarCollapsed ? 'lg:pl-20' : 'lg:pl-60'} pt-0 lg:pt-20`}>
        <AppRoutes
          user={user}
          userContext={userContext}
          systemStatus={systemStatus}
          editingOrder={editingOrder}
          onEditingOrderChange={setEditingOrder}
        />
      </main>
      <DevFloatingMenu />

      {/* Notification Drawer Overlay */}
      {isNotificationDrawerOpen && (
        <div className="fixed inset-0 z-[100] transition-opacity duration-300">
          <div
            className="absolute inset-0 overlay-backdrop"
            onClick={() => setIsNotificationDrawerOpen(false)}
          />
          <aside className={`absolute top-0 right-0 h-full w-full sm:w-[400px] transform transition-transform duration-300 ease-out border-l divider-line shadow-2xl ${isNotificationDrawerOpen ? 'translate-x-0' : 'translate-x-full'}`}>
            <NotificationFeed
              onClose={() => setIsNotificationDrawerOpen(false)}
              onUnreadCountChange={setUnreadCount}
            />
          </aside>
        </div>
      )}

      {isTaskDrawerOpen && (
        <div className="fixed inset-0 z-[100] transition-opacity duration-300">
          <div
            className="absolute inset-0 overlay-backdrop"
            onClick={() => setIsTaskDrawerOpen(false)}
          />
          <aside className={`absolute top-0 right-0 h-full w-full sm:w-[420px] transform transition-transform duration-300 ease-out border-l divider-line shadow-2xl ${isTaskDrawerOpen ? 'translate-x-0' : 'translate-x-full'}`}>
            <TaskDrawer
              onClose={() => setIsTaskDrawerOpen(false)}
              onTaskCountChange={setActiveTaskCount}
            />
          </aside>
        </div>
      )}

      {/* Gateway Connection Modal */}
      <GatewayConfigModal
        isOpen={isGatewayConfigModalOpen}
        onClose={() => setIsGatewayConfigModalOpen(false)}
        onSuccess={() => {
          setIsGatewayConfigModalOpen(false);
          fetchSystemStatus();
        }}
        currentUsername={systemControlState?.ib_username}
        currentMode={systemControlState?.ib_mode}
        savedGateways={systemControlState?.saved_gateways}
      />

      <Modal
        isOpen={isEventsModalOpen}
        onClose={() => setIsEventsModalOpen(false)}
        title="Market Events"
        width="max-w-4xl"
        flush
      >
        <MarketEvents compact />
      </Modal>

      <Modal
        isOpen={isManualOrderModalOpen}
        onClose={closeManualOrderModal}
        title={preparedNQMMOrder ? 'Review NQMM Rebalance Order' : 'Place Manual Order'}
        width="max-w-2xl"
        flush
      >
        <ManualOrderEntry
          inModal
          showCancelButton
          preparedNQMMOrder={preparedNQMMOrder}
          onCancel={closeManualOrderModal}
          onOrderSubmitted={closeManualOrderModal}
        />
      </Modal>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <GlobalErrorProvider>
          <MarketDataProvider>
            <AppContent />
          </MarketDataProvider>
        </GlobalErrorProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
